import { Product, Variant } from "../../api/decimal/methods/product.ts";
import { useIntl } from "react-intl";
import { isCourseware } from "../asset/assetHelpers.ts";
import { useDurationFormat } from "../../core-ui/useDurationFormat.ts";
import {
  isGiftCardVariant,
  isMarketplace,
  isOutOfStock,
  isPrintVariant,
} from "../../../routes/home/product/common/productUtils.ts";
import useConditionMap from "./useConditionMap.ts";

interface DisplayValues {
  shortPrice: string;
  longPrice: string;
  variantLabel: string;
  expirationDate?: Date;
}

export function useFormattedVariant(
  variant: Variant,
  product: Product
): DisplayValues {
  const intl = useIntl();
  const { formatDays, formatYears } = useDurationFormat();

  let shortPrice = isOutOfStock(variant)
    ? intl.formatMessage({ defaultMessage: "Out of Stock" })
    : intl.formatMessage({ defaultMessage: "Free" });
  let longPrice = shortPrice;

  if (isMarketplace(variant)) {
    shortPrice = intl.formatMessage({ defaultMessage: "Marketplace" });
    longPrice = shortPrice;
  } else if (variant.studentPrice) {
    shortPrice = intl.formatNumber(parseFloat(variant.studentPrice), {
      style: "currency",
      currency: variant.studentCurrency,
    });
    longPrice = `${shortPrice} ${variant.studentCurrency}`;
  }

  // For duration, first do perpetual
  let variantLabel = isCourseware(product)
    ? intl.formatMessage({ defaultMessage: "Purchase" })
    : intl.formatMessage({ defaultMessage: "Lifetime" });
  let expirationDate: Date | undefined;

  const conditionMap = useConditionMap();

  if (isGiftCardVariant(variant)) {
    variantLabel = intl.formatMessage({
      defaultMessage: "Instant Digital Delivery",
    });
  } else if (isPrintVariant(variant)) {
    if (variant.orderType === "rental") {
      variantLabel = intl.formatMessage({ defaultMessage: "Rental" });
    } else {
      variantLabel = conditionMap[variant.condition];
    }
  } else if (variant.downloadableDuration !== "perpetual") {
    const dayCount = parseInt(variant.downloadableDuration.split(" ")[0], 10);
    if (dayCount % 365 === 0) {
      variantLabel = formatYears(dayCount / 365);
    } else {
      variantLabel = formatDays(dayCount);
    }

    expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + dayCount);
  }

  return {
    shortPrice,
    longPrice,
    variantLabel,
    expirationDate,
  };
}
